/**
 * AURA Media & Motion Effects
 * Mute sounds, stop animations, hide images, and GIF pausing
 */

import { cssInjector } from '../../core/css-injector';
import { Logger } from '../../core/logger';

const logger = new Logger('MediaMotion');

const STYLE_IDS = {
  stopAnimations: 'stop-animations',
  hideImages: 'hide-images',
};

const pausedGifs = new Map<HTMLImageElement, string>();
let mediaObserver: MutationObserver | null = null;
let isMuted = false;

export function applyStopAnimations(enabled: boolean): void {
  if (enabled) {
    cssInjector.setStylesheet(
      STYLE_IDS.stopAnimations,
      `
        :where(html) body,
        :where(html) body *,
        :where(html) body *::before,
        :where(html) body *::after {
          animation: none !important;
          animation-duration: 0s !important;
          transition: none !important;
          transition-duration: 0s !important;
        }
      `,
      200
    );
    pauseAllGifs();
    logger.debug('Stop animations enabled');
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.stopAnimations);
    resumeAllGifs();
    logger.debug('Stop animations disabled');
  }
}

export function applyHideImages(enabled: boolean): void {
  if (enabled) {
    cssInjector.setStylesheet(
      STYLE_IDS.hideImages,
      `
        :where(html) body img:not([data-aura-shield]),
        :where(html) body picture,
        :where(html) body svg:not([data-aura-shield]),
        :where(html) body video {
          visibility: hidden !important;
        }
        :where(html) body [style*="background-image"] {
          background-image: none !important;
        }
      `,
      201
    );
  } else {
    cssInjector.removeStylesheet(STYLE_IDS.hideImages);
  }
}

export function applyMuteSounds(enabled: boolean): void {
  isMuted = enabled;
  if (enabled) {
    muteAllMedia();
    startMediaObserver();
  } else {
    unmuteAllMedia();
    stopMediaObserver();
  }
}

export function muteAllMedia(): void {
  document.querySelectorAll<HTMLMediaElement>('audio, video').forEach((el) => {
    el.muted = true;
  });
  suspendAudioContexts();
  logger.debug('Muted all media');
}

export function unmuteAllMedia(): void {
  document.querySelectorAll<HTMLMediaElement>('audio, video').forEach((el) => {
    el.muted = false;
  });
  resumeAudioContexts();
  logger.debug('Unmuted all media');
}

function suspendAudioContexts(): void {
  try {
    const contexts: AudioContext[] = (window as any).__auraAudioContexts ?? [];
    contexts.forEach((ctx) => {
      if (ctx.state !== 'closed') ctx.suspend();
    });
  } catch (e) {
    logger.warn('Failed to suspend AudioContexts', { error: String(e) });
  }
}

function resumeAudioContexts(): void {
  try {
    const contexts: AudioContext[] = (window as any).__auraAudioContexts ?? [];
    contexts.forEach((ctx) => {
      if (ctx.state === 'suspended') ctx.resume();
    });
  } catch (e) {
    logger.warn('Failed to resume AudioContexts', { error: String(e) });
  }
}

function startMediaObserver(): void {
  if (mediaObserver) return;
  mediaObserver = new MutationObserver((mutations) => {
    if (!isMuted) return;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches?.('audio, video')) {
          (node as HTMLMediaElement).muted = true;
        }
        node.querySelectorAll<HTMLMediaElement>('audio, video').forEach((el) => {
          el.muted = true;
        });
      }
    }
  });
  const target = document.body ?? document.documentElement;
  if (!target) {
    logger.warn('No DOM target available for media observer');
    return;
  }
  mediaObserver.observe(target, { childList: true, subtree: true });
  logger.debug('Media observer started');
}

function stopMediaObserver(): void {
  if (mediaObserver) {
    mediaObserver.disconnect();
    mediaObserver = null;
  }
}

export function pauseAllGifs(): void {
  document.querySelectorAll<HTMLImageElement>(
    'img[src$=".gif"], img[src*=".gif?"]'
  ).forEach((imgEl) => {
    if (pausedGifs.has(imgEl)) return;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = imgEl.naturalWidth || imgEl.width;
      canvas.height = imgEl.naturalHeight || imgEl.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(imgEl, 0, 0);
        pausedGifs.set(imgEl, imgEl.src);
        imgEl.src = canvas.toDataURL();
      }
    } catch (e) {
      logger.warn('Could not pause GIF (CORS)', { src: imgEl.src });
    }
  });
  logger.debug('Paused GIFs', { count: pausedGifs.size });
}

export function resumeAllGifs(): void {
  pausedGifs.forEach((originalSrc, imgEl) => {
    imgEl.src = originalSrc;
  });
  pausedGifs.clear();
  logger.debug('Resumed GIFs');
}

export function destroyMediaMotion(): void {
  stopMediaObserver();
  resumeAllGifs();
  cssInjector.removeStylesheet(STYLE_IDS.stopAnimations);
  cssInjector.removeStylesheet(STYLE_IDS.hideImages);
  isMuted = false;
}
